import React, { useEffect, useMemo, useState } from 'react';
import { LeftSide } from '../../components/LeftSide/LeftSide';
import { Navi } from '../../components/Navi/Navi';
import { RightBar } from '../../components/RightBar/RightBar';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import '../../styles/common.css';
import './Settings.css';
import {
    queryTypesFromMenuPath,
    queryTypesOrderFromMenus,
    sortMenusLikeSettings,
} from '../../utils/menuQuotaOrder';

/** 그룹별 sort_order 를 0..n-1 로 정리 (중복·공백 방지) */
function normalizeMenuSortOrders(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return rows;
    const groups = {};
    for (const m of rows) {
        const g = String(m.menu_group ?? '');
        if (!groups[g]) groups[g] = [];
        groups[g].push(m);
    }
    const orderMap = {};
    for (const g of Object.keys(groups)) {
        const arr = [...groups[g]].sort(
            (a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0)
        );
        arr.forEach((m, i) => {
            orderMap[m.menu_key] = i;
        });
    }
    return rows.map(m => ({ ...m, sort_order: orderMap[m.menu_key] ?? 0 }));
}

/** menus.flag: 0=기본 공개, 1=개별 허용만, 2=전역 비활성 */
function clampMenuFlag(flag) {
    const n = Number(flag);
    if (n === 2) return 2;
    if (n === 1) return 1;
    return 0;
}

/** DB is_new → UI용 0|1 (사용자 사이드바 N 뱃지) */
function normalizeMenuIsNewFromDb(v) {
    if (v === 'new' || v === true) return 1;
    const n = Number(v);
    return n === 1 ? 1 : 0;
}

const MENU_GROUP_ORDER = ['base', 'naver', 'tool'];


const Settings = ({ title = '환경설정' }) => {
    const [features, setFeatures] = useState([]);

    // 등급 관련 state
    const [grades, setGrades] = useState([]);
    const [selectedGrade, setSelectedGrade] = useState(null);
    const [gradeLimits, setGradeLimits] = useState({});
    const [loadingGrades, setLoadingGrades] = useState(true);
    const [loadingLimits, setLoadingLimits] = useState(false);
    const [saving, setSaving] = useState(false);
    const [gradeRowLimits, setGradeRowLimits] = useState({});

    // 등급 추가 폼
    const [showAddGrade, setShowAddGrade] = useState(false);
    const [newGradeName, setNewGradeName] = useState('');
    const [newGradeDesc, setNewGradeDesc] = useState('');
    const [addingGrade, setAddingGrade] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);
    const [editingOrder, setEditingOrder] = useState(false);

    const visibleGrades = grades.filter(g => !['manager', 'admin'].includes(g.grade_name));

    const moveGrade = (gradeName, direction) => {
        setGrades(prev => {
            const visible = prev.filter(g => !['manager', 'admin'].includes(g.grade_name));
            const hidden = prev.filter(g => ['manager', 'admin'].includes(g.grade_name));
            const idx = visible.findIndex(g => g.grade_name === gradeName);
            const newIdx = idx + direction;
            if (idx < 0 || newIdx < 0 || newIdx >= visible.length) return prev;
            const arr = [...visible];
            [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
            return [...arr, ...hidden];
        });
    };

    const handleSaveOrder = async () => {
        setSavingOrder(true);
        try {
            const order = visibleGrades.map((g, i) => ({ grade_name: g.grade_name, sort_order: i, description: g.description ?? '' }));
            const res = await api.post('/settings/grades/reorder', { order });
            if (res.data.success) toast.success('등급 순서가 저장되었습니다.');
            else toast.error(res.data.message || '저장 실패');
        } catch (err) {
            toast.error('서버 오류');
        } finally {
            setSavingOrder(false);
        }
    };

    const [menuRows, setMenuRows] = useState([]);
    const [loadingMenus, setLoadingMenus] = useState(true);
    const [savingMenus, setSavingMenus] = useState(false);

    useEffect(() => {
        api.get('/settings/features').then(res => {
            if (res.data.success) setFeatures(res.data.data);
        }).catch(() => {});
    }, []);

    const [systemSettings, setSystemSettings] = useState({});
    const [savingSystem, setSavingSystem] = useState(false);

    useEffect(() => {
        api.get('/settings/system').then(res => {
            if (res.data.success) {
                const settingsObj = {};
                res.data.data.forEach(item => {
                    settingsObj[item.setting_key] = item.setting_value;
                });
                setSystemSettings(settingsObj);
            }
        }).catch(() => {});
    }, []);

    const handleSaveSystemSettings = async () => {
        setSavingSystem(true);
        try {
            const res = await api.post('/settings/system/save', { settings: systemSettings });
            if (res.data.success) {
                toast.success('기본 환경 설정이 저장되었습니다.');
            } else {
                toast.error(res.data.message || '저장 실패');
            }
        } catch (err) {
            toast.error('서버 오류가 발생했습니다.');
        } finally {
            setSavingSystem(false);
        }
    };

    const [savingGradePrices, setSavingGradePrices] = useState(false);

    const handleSaveGradePrices = async () => {
        setSavingGradePrices(true);
        try {
            const prices = grades
                .filter(g => !['manager', 'admin'].includes(g.grade_name))
                .map(g => ({ grade_name: g.grade_name, price: g.price || 0 }));
            const res = await api.post('/settings/grades/prices', { prices });
            if (res.data.success) {
                toast.success('등급별 금액 설정이 저장되었습니다.');
            } else {
                toast.error(res.data.message || '저장 실패');
            }
        } catch (err) {
            toast.error('서버 오류가 발생했습니다.');
        } finally {
            setSavingGradePrices(false);
        }
    };

    // 등급 목록 로드
    useEffect(() => {
        const fetchGrades = async () => {
            try {
                const res = await api.get('/settings/grades');
                if (res.data.success && Array.isArray(res.data.data)) {
                    setGrades(res.data.data);
                    const visible = res.data.data.filter(g => !['manager', 'admin'].includes(g.grade_name));
                    if (visible.length > 0) setSelectedGrade(visible[0].grade_name);
                }
            } catch (err) {
                toast.error('등급 목록 불러오기 실패');
                console.error(err);
            } finally {
                setLoadingGrades(false);
            }
        };
        fetchGrades();
    }, []);

    // 선택 등급 변경 시 한도 로드 (조회 횟수 + 행수 동시)
    useEffect(() => {
        if (!selectedGrade) return;
        const fetchLimits = async () => {
            setLoadingLimits(true);
            try {
                const [qRes, rRes] = await Promise.all([
                    api.get(`/settings/grade-limits/${selectedGrade}`),
                    api.get(`/settings/grade-row-limits/${selectedGrade}`),
                ]);
                if (qRes.data.success) {
                    const limitsMap = Object.fromEntries(features.map(f => [f.queryType, 0]));
                    qRes.data.data.forEach(item => {
                        if (Object.prototype.hasOwnProperty.call(limitsMap, item.query_type)) {
                            limitsMap[item.query_type] = item.daily_limit;
                        }
                    });
                    setGradeLimits(limitsMap);
                }
                if (rRes.data.success) {
                    const rowObj = {};
                    rRes.data.data.forEach(item => {
                        rowObj[String(item.gubun).toLowerCase()] = item.max_rows;
                    });
                    setGradeRowLimits(rowObj);
                }
            } catch (err) {
                toast.error('한도 불러오기 실패');
                console.error(err);
            } finally {
                setLoadingLimits(false);
            }
        };
        fetchLimits();
    }, [selectedGrade]);

    useEffect(() => {
        let cancelled = false;
        const fetchMenus = async () => {
            try {
                const res = await api.get('/settings/menus');
                if (cancelled) return;
                if (res.data?.success && Array.isArray(res.data.data)) {
                    setMenuRows(
                        normalizeMenuSortOrders(
                            res.data.data.map(m => ({
                                ...m,
                                flag: clampMenuFlag(m.flag),
                                is_new: normalizeMenuIsNewFromDb(m.is_new),
                                rank_row_gubun:
                                    m.rank_row_gubun != null ? String(m.rank_row_gubun) : '',
                            }))
                        )
                    );
                } else {
                    toast.error(res.data?.message || '메뉴 목록 응답이 올바르지 않습니다.');
                }
            } catch (err) {
                if (cancelled) return;
                console.error(err);
                const msg =
                    err.response?.data?.message ||
                    (typeof err.response?.data === 'string' ? null : err.message);
                toast.error(
                    msg ||
                        '메뉴 목록을 불러오지 못했습니다. 서버를 최신 코드로 배포했는지 확인하세요.'
                );
            } finally {
                if (!cancelled) setLoadingMenus(false);
            }
        };
        fetchMenus();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleMenuFieldChange = (menuKey, field, value) => {
        setMenuRows(prev =>
            prev.map(row => (row.menu_key === menuKey ? { ...row, [field]: value } : row))
        );
    };

    /** 같은 menu_group 안에서 한 칸 위/아래로 맞바꾼 뒤 sort_order 재부여 */
    const moveMenuInGroup = (menuKey, direction) => {
        setMenuRows(prev => {
            const target = prev.find(m => m.menu_key === menuKey);
            if (!target) return prev;
            const g = String(target.menu_group ?? '');
            const inGroup = prev
                .filter(m => String(m.menu_group ?? '') === g)
                .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));
            const idx = inGroup.findIndex(m => m.menu_key === menuKey);
            const newIdx = idx + direction;
            if (idx < 0 || newIdx < 0 || newIdx >= inGroup.length) return prev;
            const arr = [...inGroup];
            [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
            const map = {};
            arr.forEach((m, i) => {
                map[m.menu_key] = i;
            });
            return prev.map(m =>
                String(m.menu_group ?? '') === g ? { ...m, sort_order: map[m.menu_key] } : m
            );
        });
    };

    const sortedMenuRows = useMemo(() => {
        const rank = grp => {
            const i = MENU_GROUP_ORDER.indexOf(String(grp));
            return i >= 0 ? i : 999;
        };
        return [...menuRows].sort((a, b) => {
            const ga = String(a.menu_group ?? '');
            const gb = String(b.menu_group ?? '');
            if (ga !== gb) return rank(ga) - rank(gb);
            return (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
        });
    }, [menuRows]);

    const menuMoveFlags = useMemo(() => {
        const up = {};
        const down = {};
        const byGroup = {};
        for (const m of sortedMenuRows) {
            const g = String(m.menu_group ?? '');
            if (!byGroup[g]) byGroup[g] = [];
            byGroup[g].push(m.menu_key);
        }
        for (const g of Object.keys(byGroup)) {
            const keys = byGroup[g];
            keys.forEach((key, i) => {
                up[key] = i > 0;
                down[key] = i < keys.length - 1;
            });
        }
        return { up, down };
    }, [sortedMenuRows]);

    /**
     * 기본 한도 행: MENUS.path → query_type 매핑에 있는 것만, 사이드바 순서.
     * 메뉴에서 뺀 기능(예: 매출·지수)은 여기서도 숨김. 메뉴가 비어 있으면 limitConfig 전체(폴백).
     */
    /** query_type → 해당 기능을 대표하는 menus.menu_key (사이드바 순서 우선) */
    const queryTypeToMenuKey = useMemo(() => {
        const map = {};
        if (!Array.isArray(menuRows) || menuRows.length === 0) return map;
        for (const m of sortMenusLikeSettings(menuRows)) {
            const qts = queryTypesFromMenuPath(m.path);
            for (const qt of qts) {
                if (qt && !Object.prototype.hasOwnProperty.call(map, qt)) {
                    map[qt] = m.menu_key || '';
                }
            }
        }
        return map;
    }, [menuRows]);

    const orderedLimitRows = useMemo(() => {
        const byKey = Object.fromEntries(features.map(f => [f.queryType, f]));
        if (!Array.isArray(menuRows) || menuRows.length === 0) {
            return features.map(f => ({
                key: f.queryType,
                label: f.label,
                menuKey: queryTypeToMenuKey[f.queryType] || '',
            }));
        }
        const menuLabelByKey = Object.fromEntries(menuRows.map(m => [m.menu_key, m.label]));
        const keys = queryTypesOrderFromMenus(menuRows).filter(k => byKey[k]);

        const menuKeyCount = {};
        for (const key of keys) {
            const mk = queryTypeToMenuKey[key] || '';
            menuKeyCount[mk] = (menuKeyCount[mk] || 0) + 1;
        }

        return keys.map(key => {
            const menuKey = queryTypeToMenuKey[key] || '';
            const menuLabel = menuLabelByKey[menuKey];
            const label = menuLabel && menuKeyCount[menuKey] === 1 ? menuLabel : byKey[key].label;
            return { key, label, menuKey };
        });
    }, [features, menuRows, queryTypeToMenuKey]);

    const visibleLimitKeys = useMemo(() => orderedLimitRows.map(r => r.key), [orderedLimitRows]);

    const handleSaveMenus = async () => {
        setSavingMenus(true);
        try {
            const items = menuRows.map(m => ({
                menu_key: m.menu_key,
                label: m.label,
                sort_order: Number(m.sort_order),
                flag: clampMenuFlag(m.flag),
                use_yn:
                    String(m.use_yn ?? 'Y')
                        .trim()
                        .toUpperCase() === 'N'
                        ? 'N'
                        : 'Y',
                is_new: normalizeMenuIsNewFromDb(m.is_new),
                rank_row_gubun: m.rank_row_gubun ?? '',
            }));
            const res = await api.post('/settings/menus/save', { items });
            if (res.data.success) {
                toast.success('메뉴 설정이 저장되었습니다.');
            } else {
                toast.error(res.data.message || '저장 실패');
            }
        } catch (err) {
            console.error(err);
            const msg = err.response?.data?.message;
            toast.error(msg || '메뉴 저장 중 오류가 발생했습니다.');
        } finally {
            setSavingMenus(false);
        }
    };

    const handleLimitChange = (key, value) => {
        setGradeLimits(prev => ({ ...prev, [key]: value }));
    };

    const handleSaveLimits = async () => {
        if (!selectedGrade) return;
        setSaving(true);
        try {
            const queryData = {};
            for (const k of visibleLimitKeys) {
                const raw = gradeLimits[k];
                const n = raw === '' || raw === null || raw === undefined ? 0 : Number(raw);
                queryData[k] = Number.isNaN(n) || n < 0 ? 0 : Math.floor(n);
            }

            // 행수 한도: rank_row_gubun 체크된 메뉴만, 키는 실제 gubun 사용
            const rowData = {};
            for (const { menuKey } of orderedLimitRows) {
                const row = menuRows.find(m => m.menu_key === menuKey);
                if (!row || !row.rank_row_gubun) continue;
                const gubuns = String(row.rank_row_gubun).split(',').map(s => s.trim()).filter(Boolean);
                if (gubuns.length === 0) continue;
                const primaryGubun = gubuns[0];
                const raw = gradeRowLimits[primaryGubun.toLowerCase()];
                const n = raw === '' || raw === null || raw === undefined ? 0 : Number(raw);
                const limitVal = Number.isNaN(n) || n < 0 ? 0 : Math.floor(n);
                for (const g of gubuns) {
                    rowData[g.toLowerCase()] = limitVal;
                }
            }

            const [qRes, rRes] = await Promise.all([
                api.post('/settings/grade-limits/save', { grade_name: selectedGrade, data: queryData }),
                Object.keys(rowData).length > 0
                    ? api.post('/settings/grade-row-limits/save', { grade_name: selectedGrade, data: rowData })
                    : Promise.resolve({ data: { success: true } }),
            ]);

            if (qRes.data.success && rRes.data.success) {
                setGradeLimits(prev => ({ ...prev, ...queryData }));
                setGradeRowLimits(prev => ({ ...prev, ...rowData }));
                toast.success(`[${selectedGrade}] 등급 한도가 저장되었습니다.`);
            } else {
                toast.error('저장 실패: ' + (qRes.data.message || rRes.data.message || ''));
            }
        } catch (err) {
            console.error(err);
            toast.error('서버 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleAddGrade = async () => {
        if (!newGradeName.trim()) return;
        setAddingGrade(true);
        try {
            const res = await api.post('/settings/grades/add', {
                grade_name: newGradeName.trim(),
                description: newGradeDesc.trim(),
            });
            if (res.data.success) {
                toast.success('등급이 추가되었습니다.');
                const refreshed = await api.get('/settings/grades');
                if (refreshed.data.success) setGrades(refreshed.data.data);
                setSelectedGrade(newGradeName.trim());
                setNewGradeName('');
                setNewGradeDesc('');
                setShowAddGrade(false);
            } else {
                toast.error(res.data.message || '등급 추가 실패');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || '서버 오류');
        } finally {
            setAddingGrade(false);
        }
    };

    const handleDeleteGrade = async (gradeName) => {
        if (!window.confirm(`'${gradeName}' 등급을 삭제할까요?`)) return;
        try {
            const res = await api.delete(`/settings/grades/${gradeName}`);
            if (res.data.success) {
                toast.success('등급이 삭제되었습니다.');
                const refreshed = await api.get('/settings/grades');
                if (refreshed.data.success) {
                    setGrades(refreshed.data.data);
                    const visible = refreshed.data.data.filter(g => !['manager', 'admin'].includes(g.grade_name));
                    setSelectedGrade(visible[0]?.grade_name ?? null);
                }
            } else {
                toast.error(res.data.message || '삭제 실패');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || '서버 오류');
        }
    };

    return (
        <div id="wrapper">
            <Navi title={title} />
            <LeftSide />
            <div className="content-page">
                <div className="content">
                    <div className="container-fluid settings-admin-page">
                        <div className="row settings-layout-row gx-4 gy-4 align-items-start">
                            <div className="col-12 col-xl-5 col-xxl-4 settings-col-limits">
                                <div className="card settings-card">
                                    <div className="card-body">
                                        <div className="settings-card-head">
                                            <div className="settings-card-icon" aria-hidden>
                                                <i className="mdi mdi-cog-outline"></i>
                                            </div>
                                            <h4 className="settings-card-title">
                                                등급별 조회 한도 설정
                                            </h4>
                                        </div>

                                        {loadingGrades ? (
                                            <div className="settings-loading">불러오는 중...</div>
                                        ) : (
                                            <>
                                                {/* 등급 탭 */}
                                                <div className="d-flex flex-wrap align-items-center gap-1 mb-2">
                                                    {visibleGrades.map(g => (
                                                        <button
                                                            key={g.grade_name}
                                                            type="button"
                                                            className={`btn btn-sm ${selectedGrade === g.grade_name ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                            onClick={() => setSelectedGrade(g.grade_name)}
                                                        >
                                                            {g.grade_name}
                                                            {g.description ? ` (${g.description})` : ''}
                                                            <span className="ms-1 badge bg-secondary" style={{ fontSize: '0.7em' }}>
                                                                {g.user_count ?? 0}명
                                                            </span>
                                                        </button>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-success"
                                                        onClick={() => setShowAddGrade(v => !v)}
                                                    >
                                                        + 등급 추가
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => setEditingOrder(v => !v)}
                                                    >
                                                        순서 편집
                                                    </button>
                                                    {selectedGrade && !['user', 'admin'].includes(selectedGrade) && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDeleteGrade(selectedGrade)}
                                                        >
                                                            삭제
                                                        </button>
                                                    )}
                                                </div>

                                                {/* 순서 편집 패널 */}
                                                {editingOrder && (
                                                    <div className="border rounded p-2 mb-2 bg-light">
                                                        <div className="small text-muted mb-2">순서 조정 및 설명 편집 후 저장하세요.</div>
                                                        {visibleGrades.map((g, idx) => (
                                                            <div key={g.grade_name} className="d-flex align-items-center gap-2 py-1 border-bottom">
                                                                <code className="small text-dark" style={{ minWidth: 70 }}>{g.grade_name}</code>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="설명"
                                                                    value={g.description ?? ''}
                                                                    onChange={e => setGrades(prev => prev.map(p =>
                                                                        p.grade_name === g.grade_name ? { ...p, description: e.target.value } : p
                                                                    ))}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-xs btn-outline-secondary py-0 px-1"
                                                                    disabled={idx === 0}
                                                                    onClick={() => moveGrade(g.grade_name, -1)}
                                                                >▲</button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-xs btn-outline-secondary py-0 px-1"
                                                                    disabled={idx === visibleGrades.length - 1}
                                                                    onClick={() => moveGrade(g.grade_name, 1)}
                                                                >▼</button>
                                                            </div>
                                                        ))}
                                                        <div className="mt-2 d-flex gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                onClick={async () => { await handleSaveOrder(); setEditingOrder(false); }}
                                                                disabled={savingOrder}
                                                            >
                                                                {savingOrder ? '저장 중...' : '순서 저장'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={() => setEditingOrder(false)}
                                                            >
                                                                취소
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* 등급 추가 폼 */}
                                                {showAddGrade && (
                                                    <div className="border rounded p-2 mb-2 bg-light">
                                                        <div className="d-flex gap-2 mb-1">
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="등급명 (영문, 예: vip)"
                                                                value={newGradeName}
                                                                onChange={e => setNewGradeName(e.target.value)}
                                                            />
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="설명 (예: VIP 사용자)"
                                                                value={newGradeDesc}
                                                                onChange={e => setNewGradeDesc(e.target.value)}
                                                            />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-success"
                                                            onClick={handleAddGrade}
                                                            disabled={addingGrade || !newGradeName.trim()}
                                                        >
                                                            {addingGrade ? '추가 중...' : '추가'}
                                                        </button>
                                                    </div>
                                                )}

                                                <p className="settings-hint settings-limit-zero-hint mb-2">
                                                    <strong>0</strong> ={' '}
                                                    <span className="settings-unlimited-em">무제한</span>
                                                    · 신규 가입자 등급 기본값으로 쓰입니다.
                                                </p>

                                                {loadingLimits ? (
                                                    <div className="settings-loading">한도 불러오는 중...</div>
                                                ) : (
                                                    <div className="mt-1">
                                                        <div className="settings-limit-row settings-limit-header-row">
                                                            <div className="settings-limit-label">
                                                                <span className="settings-limit-label-title text-muted small">메뉴</span>
                                                            </div>
                                                            <div className="settings-limit-input-wrap d-flex gap-1 align-items-center">
                                                                <span className="settings-limit-col-head">행수</span>
                                                                <span className="settings-limit-col-head">조회 횟수</span>
                                                            </div>
                                                        </div>
                                                        {orderedLimitRows.map(({ label, key, menuKey }) => {
                                                            const menuRow = menuRows.find(m => m.menu_key === menuKey);
                                                            const hasRow = !!(menuRow?.rank_row_gubun);
                                                            const gubuns = hasRow ? String(menuRow.rank_row_gubun).split(',').map(s => s.trim()).filter(Boolean) : [];
                                                            const primaryGubun = gubuns.length > 0 ? gubuns[0] : null;

                                                            const v = gradeLimits[key];
                                                            const n = Number(v);
                                                            const isUnlimited = v === '' || v === null || v === undefined || (!Number.isNaN(n) && n === 0);

                                                            const rv = primaryGubun ? (gradeRowLimits[primaryGubun.toLowerCase()] ?? '') : null;
                                                            const rn = Number(rv);
                                                            const isRowUnlimited = rv === '' || rv === null || rv === undefined || (!Number.isNaN(rn) && rn === 0);

                                                            return (
                                                                <div key={key} className="settings-limit-row">
                                                                    <label className="settings-limit-label">
                                                                        <span className="settings-limit-label-title">{label}</span>
                                                                        {menuKey ? (
                                                                            <span className="settings-limit-menu-key-wrap" title="menu_key">
                                                                                <code>{menuKey}</code>
                                                                            </span>
                                                                        ) : (
                                                                            <span className="settings-limit-menu-key-wrap settings-limit-menu-key-fallback" title="query_type">
                                                                                <code>{key}</code>
                                                                            </span>
                                                                        )}
                                                                    </label>
                                                                    <div className="settings-limit-input-wrap d-flex gap-1 align-items-center">
                                                                        <div className="settings-limit-single-wrap">
                                                                            {hasRow ? (
                                                                                <>
                                                                                    {isRowUnlimited && (
                                                                                        <span className="settings-limit-unlimited-badge" title="0 = 무제한">∞</span>
                                                                                    )}
                                                                                    <input
                                                                                        type="number"
                                                                                        min={0}
                                                                                        className="settings-limit-input"
                                                                                        value={rv === '' || rv === null || rv === undefined ? '' : rv}
                                                                                        placeholder="0"
                                                                                        onChange={e => {
                                                                                            const val = e.target.value === '' ? '' : e.target.value;
                                                                                            if (primaryGubun) {
                                                                                                setGradeRowLimits(prev => {
                                                                                                    const next = { ...prev };
                                                                                                    gubuns.forEach(g => { next[g.toLowerCase()] = val; });
                                                                                                    return next;
                                                                                                });
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </>
                                                                            ) : (
                                                                                <span className="settings-limit-na">-</span>
                                                                            )}
                                                                        </div>
                                                                        <div className="settings-limit-single-wrap">
                                                                            {isUnlimited && (
                                                                                <span className="settings-limit-unlimited-badge" title="0 = 무제한">∞</span>
                                                                            )}
                                                                            <input
                                                                                id={`limit-${key}`}
                                                                                type="number"
                                                                                min={0}
                                                                                className="settings-limit-input"
                                                                                value={v === '' || v === null || v === undefined ? '' : v}
                                                                                placeholder="0"
                                                                                onChange={e =>
                                                                                    handleLimitChange(key, e.target.value === '' ? '' : e.target.value)
                                                                                }
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                <div className="settings-actions">
                                                    <button
                                                        type="button"
                                                        className="btn btn-settings-primary"
                                                        onClick={handleSaveLimits}
                                                        disabled={saving || !selectedGrade}
                                                    >
                                                        {saving ? '저장 중...' : '저장'}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 col-xl-7 col-xxl-8 settings-col-menus">
                                <div className="card settings-card">
                                    <div className="card-body">
                                        <div className="settings-card-head">
                                            <div className="settings-card-icon" aria-hidden>
                                                <i className="mdi mdi-view-list-outline"></i>
                                            </div>
                                            <h4 className="settings-card-title">
                                                사용자 사이드바 메뉴
                                            </h4>
                                        </div>
                                        <p className="settings-hint mb-3">
                                            순서 변경·노출 방식·N뱃지·행수 한도 설정 후 메뉴 저장. 반영은 <strong>재로그인</strong> 후.
                                        </p>

                                        {loadingMenus ? (
                                            <div className="settings-loading">
                                                메뉴 불러오는 중...
                                            </div>
                                        ) : (
                                            <>
                                                <div className="settings-menu-table-outer">
                                                    <div className="table-responsive settings-menu-table-inner">
                                                        <table className="table table-sm table-bordered table-hover align-middle settings-menu-table">
                                                            <thead>
                                                                <tr className="text-center">
                                                                    <th>menu_key</th>
                                                                    <th>메뉴명</th>
                                                                    <th>그룹</th>
                                                                    <th
                                                                        style={{
                                                                            width: 58,
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        순서
                                                                    </th>
                                                                    <th style={{ width: 80 }}>
                                                                        사용
                                                                    </th>
                                                                    <th
                                                                        style={{
                                                                            width: 88,
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                        title="사이드바 메뉴 옆 N 뱃지"
                                                                    >
                                                                        N표시
                                                                    </th>
                                                                    <th style={{ minWidth: 220 }}>
                                                                        노출 방식
                                                                    </th>
                                                                    <th
                                                                        style={{ width: 70 }}
                                                                        className="text-center"
                                                                        title="행수 한도 적용 여부"
                                                                    >
                                                                        행수 한도
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {sortedMenuRows.map(m => (
                                                                    <tr key={m.menu_key}>
                                                                        <td className="small font-monospace">
                                                                            {m.menu_key}
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                value={
                                                                                    m.label ?? ''
                                                                                }
                                                                                onChange={e =>
                                                                                    handleMenuFieldChange(
                                                                                        m.menu_key,
                                                                                        'label',
                                                                                        e.target
                                                                                            .value
                                                                                    )
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td className="small fw-semibold">
                                                                            {m.menu_group}
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <div
                                                                                className="settings-sort-arrows"
                                                                                role="group"
                                                                                aria-label="같은 그룹 안에서 순서 이동"
                                                                            >
                                                                                <button
                                                                                    type="button"
                                                                                    className="settings-sort-arrow-btn"
                                                                                    title="위로"
                                                                                    disabled={
                                                                                        !menuMoveFlags
                                                                                            .up[
                                                                                            m
                                                                                                .menu_key
                                                                                        ]
                                                                                    }
                                                                                    onClick={() =>
                                                                                        moveMenuInGroup(
                                                                                            m.menu_key,
                                                                                            -1
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <i className="fas fa-chevron-up" />
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="settings-sort-arrow-btn"
                                                                                    title="아래로"
                                                                                    disabled={
                                                                                        !menuMoveFlags
                                                                                            .down[
                                                                                            m
                                                                                                .menu_key
                                                                                        ]
                                                                                    }
                                                                                    onClick={() =>
                                                                                        moveMenuInGroup(
                                                                                            m.menu_key,
                                                                                            1
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <i className="fas fa-chevron-down" />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <select
                                                                                className="form-select form-select-sm"
                                                                                value={
                                                                                    String(
                                                                                        m.use_yn ??
                                                                                            'Y'
                                                                                    ).toUpperCase() ===
                                                                                    'N'
                                                                                        ? 'N'
                                                                                        : 'Y'
                                                                                }
                                                                                onChange={e =>
                                                                                    handleMenuFieldChange(
                                                                                        m.menu_key,
                                                                                        'use_yn',
                                                                                        e.target
                                                                                            .value
                                                                                    )
                                                                                }
                                                                            >
                                                                                <option value="Y">
                                                                                    Y
                                                                                </option>
                                                                                <option value="N">
                                                                                    N
                                                                                </option>
                                                                            </select>
                                                                        </td>
                                                                        <td>
                                                                            <select
                                                                                className="form-select form-select-sm"
                                                                                value={normalizeMenuIsNewFromDb(
                                                                                    m.is_new
                                                                                )}
                                                                                onChange={e =>
                                                                                    handleMenuFieldChange(
                                                                                        m.menu_key,
                                                                                        'is_new',
                                                                                        Number(
                                                                                            e.target
                                                                                                .value
                                                                                        )
                                                                                    )
                                                                                }
                                                                            >
                                                                                <option value={0}>
                                                                                    끔
                                                                                </option>
                                                                                <option value={1}>
                                                                                    켬
                                                                                </option>
                                                                            </select>
                                                                        </td>
                                                                        <td>
                                                                            <select
                                                                                className="form-select form-select-sm"
                                                                                value={clampMenuFlag(
                                                                                    m.flag
                                                                                )}
                                                                                onChange={e =>
                                                                                    handleMenuFieldChange(
                                                                                        m.menu_key,
                                                                                        'flag',
                                                                                        Number(
                                                                                            e.target
                                                                                                .value
                                                                                        )
                                                                                    )
                                                                                }
                                                                            >
                                                                                <option value={0}>
                                                                                    기본 공개 (개별
                                                                                    차단 가능)
                                                                                </option>
                                                                                <option value={1}>
                                                                                    개별 허용만
                                                                                    (지정 사용자만)
                                                                                </option>
                                                                                <option value={2}>
                                                                                    전역 비활성
                                                                                </option>
                                                                            </select>
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={!!(m.rank_row_gubun)}
                                                                                onChange={e =>
                                                                                    handleMenuFieldChange(
                                                                                        m.menu_key,
                                                                                        'rank_row_gubun',
                                                                                        e.target.checked ? m.menu_key : ''
                                                                                    )
                                                                                }
                                                                            />
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                                <div className="settings-actions">
                                                    <button
                                                        type="button"
                                                        className="btn btn-settings-primary"
                                                        onClick={handleSaveMenus}
                                                        disabled={
                                                            savingMenus || menuRows.length === 0
                                                        }
                                                    >
                                                        {savingMenus ? '저장 중...' : '메뉴 저장'}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* 기본 환경 설정 패널 */}
                            <div className="col-12 mb-4">
                                <div className="card settings-card">
                                    <div className="card-body">
                                        <div className="settings-card-head">
                                            <div className="settings-card-icon" aria-hidden>
                                                <i className="mdi mdi-cogs"></i>
                                            </div>
                                            <h4 className="settings-card-title">기본 환경 설정</h4>
                                        </div>
                                        <div className="row mt-3">
                                            <div className="col-md-3">
                                                <div className="mb-3">
                                                    <label className="form-label">은행명</label>
                                                    <input 
                                                        type="text" 
                                                        className="form-control" 
                                                        placeholder="예: 국민은행" 
                                                        value={systemSettings.bank_name || ''}
                                                        onChange={e => setSystemSettings(prev => ({ ...prev, bank_name: e.target.value }))}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-5">
                                                <div className="mb-3">
                                                    <label className="form-label">계좌번호</label>
                                                    <input 
                                                        type="text" 
                                                        className="form-control border-primary" 
                                                        placeholder="예: 123-456-789012" 
                                                        value={systemSettings.bank_account || ''}
                                                        onChange={e => setSystemSettings(prev => ({ ...prev, bank_account: e.target.value }))}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="mb-3">
                                                    <label className="form-label">예금주</label>
                                                    <input 
                                                        type="text" 
                                                        className="form-control" 
                                                        placeholder="예: 대표자명" 
                                                        value={systemSettings.bank_holder || ''}
                                                        onChange={e => setSystemSettings(prev => ({ ...prev, bank_holder: e.target.value }))}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <small className="text-muted">무통장 입금 신청 시 사용자에게 노출되는 입금 안내 정보입니다.</small>
                                            </div>
                                        </div>
                                        <div className="text-start mt-1">
                                            <button 
                                                type="button" 
                                                className="btn btn-settings-primary"
                                                onClick={handleSaveSystemSettings}
                                                disabled={savingSystem}
                                            >
                                                {savingSystem ? '저장 중...' : '설정 저장'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* 등급별 금액 관리 패널 */}
                            <div className="col-12 mb-4">
                                <div className="card settings-card">
                                    <div className="card-body">
                                        <div className="settings-card-head">
                                            <div className="settings-card-icon" aria-hidden>
                                                <i className="mdi mdi-currency-krw"></i>
                                            </div>
                                            <h4 className="settings-card-title">등급별 금액 관리</h4>
                                        </div>
                                        <div className="row mt-3">
                                            {grades.filter(g => !['manager', 'admin'].includes(g.grade_name)).map((g) => (
                                                <div key={g.grade_name} className="col-md-3">
                                                    <div className="mb-3">
                                                        <label className="form-label">{g.grade_name} ({g.description})</label>
                                                        <div className="input-group">
                                                            <input 
                                                                type="number" 
                                                                className="form-control" 
                                                                placeholder="예: 30000" 
                                                                value={g.price || ''}
                                                                onChange={e => setGrades(prev => prev.map(p => p.grade_name === g.grade_name ? { ...p, price: e.target.value } : p))}
                                                            />
                                                            <span className="input-group-text">원</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="col-12">
                                                <small className="text-muted">요금안내 페이지 및 결제 요청 시 노출되는 등급별 월별 구독 금액입니다.</small>
                                            </div>
                                        </div>
                                        <div className="text-start mt-1">
                                            <button 
                                                type="button" 
                                                className="btn btn-settings-primary"
                                                onClick={handleSaveGradePrices}
                                                disabled={savingGradePrices}
                                            >
                                                {savingGradePrices ? '저장 중...' : '금액 설정 저장'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <RightBar />
            </div>
        </div>
    );
};

export { Settings };
